import { z } from "zod";
import { hc } from "hono/client";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { withAuth } from "@/utils/withAuth";
import type { AuditRoutes } from "server/audit"; // Adjust path as needed
import { superUserClient } from "@/features-root-user/service/user";

export const organizationAuditOperationType = z.enum([
  "INSERT",
  "UPDATE",
  "DELETE",
]);

const auditClient = hc<AuditRoutes>(
  import.meta.env.VITE_SERVER_URL + "v1/super-admin/audit",
);

// Schema matching the backend response for a single log entry
const auditLogEntrySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  organizationName: z.string().nullable(),
  oldData: z.record(z.unknown()).nullable(),
  newData: z.record(z.unknown()).nullable(),
  updatedBy: z.string(),
  updatedByDisplayName: z.string(),
  updatedAt: z.coerce.date(), // Coerce string to Date
  operationType: organizationAuditOperationType,
});

// Schema for the paginated response, matching backend
const paginatedAuditLogsSchema = z.object({
  logs: z.array(auditLogEntrySchema),
  totalCount: z.number(),
  page: z.number(),
  pageSize: z.number(),
});

export type AuditLogEntry = z.infer<typeof auditLogEntrySchema>;
export type PaginatedAuditLogsResponse = z.infer<
  typeof paginatedAuditLogsSchema
>;

interface FetchAuditLogsParams {
  page?: number;
  pageSize?: number;
  organizationId?: string;
  operationType?: z.infer<typeof organizationAuditOperationType>;
  updatedBy?: string;
}

/**
 * Fetch paginated audit logs from the server.
 * Only pagination is handled server-side, all filtering is done client-side.
 */
async function fetchAuditLogs(
  params: FetchAuditLogsParams = {},
): Promise<PaginatedAuditLogsResponse> {
  return withAuth(async (headers) => {
    const queryParams: Record<string, string> = {};
    // Only send pagination params to the server
    if (params.page) queryParams.page = String(params.page);
    if (params.pageSize) queryParams.pageSize = String(params.pageSize);

    const response = await auditClient.index.$get(
      { query: queryParams },
      { headers },
    );
    if (!response.ok) {
      throw new Error("Failed to fetch audit logs");
    }
    const data = await response.json();
    // Validate the response data against the Zod schema
    return paginatedAuditLogsSchema.parse(data);
  });
}

/**
 * React Query options for fetching audit logs.
 */
export const auditLogsQueryOptions = (params: FetchAuditLogsParams) =>
  queryOptions({
    queryKey: ["auditLogs", params], // Include params in queryKey for caching
    queryFn: () => fetchAuditLogs(params),
    placeholderData: (previousData) => previousData, // Keep previous data while loading new page
    staleTime: 1000 * 30, // 30 seconds
  });

/**
 * Custom hook to fetch audit logs using TanStack Query.
 */
export function useAuditLogs(params: FetchAuditLogsParams) {
  return useQuery(auditLogsQueryOptions(params));
}
